import { useState, useCallback, useRef } from "react";
import {
  getSheetsAccessToken,
  hasGoogleSheetsScope,
  isGoogle,
  SheetsReauthorizeRequiredError,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

const GOOGLE_API_SCRIPT = "https://apis.google.com/js/api.js";
const SCRIPT_LOAD_TIMEOUT = 15_000;

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  msg: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(msg)), ms),
    ),
  ]);
}

export interface PickerResult {
  spreadsheetId: string;
  name: string;
}

/** Structured outcome of `openPicker`. Callers branch on `kind` rather than
 *  guessing whether a `null` result meant "user cancelled" or "scope was
 *  missing". `needs-sheets-reauth` is the gate that prevents the Picker
 *  iframe from receiving a token without `drive.file` and producing the
 *  un-catchable Google 403 page. */
export type OpenPickerOutcome =
  | { kind: "picked"; result: PickerResult }
  | { kind: "cancelled" }
  | { kind: "needs-sheets-reauth" }
  | { kind: "needs-reauth" }
  | { kind: "error"; message: string };

let gapiLoadPromise: Promise<void> | null = null;

function loadGapiScript(): Promise<void> {
  if (gapiLoadPromise) return gapiLoadPromise;

  gapiLoadPromise = new Promise<void>((resolve, reject) => {
    if (window.gapi?.client) {
      resolve();
      return;
    }

    const existing = document.querySelector(
      `script[src="${GOOGLE_API_SCRIPT}"]`,
    );
    if (existing) {
      const check = () => {
        if (window.gapi) resolve();
        else setTimeout(check, 50);
      };
      check();
      return;
    }

    const script = document.createElement("script");
    script.src = GOOGLE_API_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gapiLoadPromise = null;
      reject(new Error("Failed to load Google API script"));
    };
    document.head.appendChild(script);
  });

  return gapiLoadPromise;
}

function loadPickerApi(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    window.gapi.load("picker", {
      callback: () => resolve(),
      onerror: () => reject(new Error("Failed to load Picker API")),
    });
  });
}

export const useGooglePicker = (connection: SavedConnection | null) => {
  const [isPickerLoading, setIsPickerLoading] = useState(false);
  const pickerApiLoaded = useRef(false);

  const googleCreds =
    connection && isGoogle(connection.credentials)
      ? connection.credentials
      : null;
  const needsSheetsReauth =
    googleCreds !== null && !hasGoogleSheetsScope(googleCreds);

  const openPicker = useCallback(async (): Promise<OpenPickerOutcome> => {
    setIsPickerLoading(true);

    if (!connection || !googleCreds) {
      setIsPickerLoading(false);
      return { kind: "error", message: "Pick a Google account first." };
    }

    if (needsSheetsReauth) {
      setIsPickerLoading(false);
      return { kind: "needs-sheets-reauth" };
    }

    try {
      const accessToken = await getSheetsAccessToken(connection.id);
      if (!accessToken) {
        setIsPickerLoading(false);
        return {
          kind: "error",
          message:
            "Failed to fetch Google access token. Try Reauthorize on the connection.",
        };
      }

      if (!pickerApiLoaded.current) {
        await withTimeout(
          loadGapiScript(),
          SCRIPT_LOAD_TIMEOUT,
          "Google API script timed out",
        );
        await withTimeout(
          loadPickerApi(),
          SCRIPT_LOAD_TIMEOUT,
          "Picker API timed out",
        );
        pickerApiLoaded.current = true;
      }

      const apiKey = import.meta.env.VITE_APP_GOOGLE_PICKER_ID;

      return new Promise<OpenPickerOutcome>((resolve) => {
        const view = new google.picker.DocsView(
          google.picker.ViewId.SPREADSHEETS,
        );
        view.setMimeTypes("application/vnd.google-apps.spreadsheet");

        const appId = import.meta.env.VITE_APP_GOOGLE_APP_ID;

        const picker = new google.picker.PickerBuilder()
          .addView(view)
          .setOAuthToken(accessToken)
          .setDeveloperKey(apiKey)
          .setAppId(appId)
          .setOrigin(window.location.protocol + "//" + window.location.host)
          .setCallback((data: google.picker.ResponseObject) => {
            if (data.action === google.picker.Action.PICKED) {
              const doc = data.docs[0];
              resolve({
                kind: "picked",
                result: {
                  spreadsheetId: doc.id,
                  name: doc.name,
                },
              });
            } else if (data.action === google.picker.Action.CANCEL) {
              resolve({ kind: "cancelled" });
            }
          })
          .setTitle("Select a Google Spreadsheet")
          .build();

        setIsPickerLoading(false);
        picker.setVisible(true);
      });
    } catch (err) {
      setIsPickerLoading(false);
      if (err instanceof SheetsReauthorizeRequiredError) {
        return { kind: "needs-reauth" };
      }
      const message =
        err instanceof Error ? err.message : "Failed to open file picker";
      return { kind: "error", message };
    }
  }, [connection, googleCreds, needsSheetsReauth]);

  return { openPicker, isPickerLoading, needsSheetsReauth };
};
